import {
  PermanentBaseViewModel,
  PermanentCreateViewModel,
  PermanentDetailViewModel,
  PermanentEditViewModel,
  ProductCreateViewModel,
  ProductEditViewModel,
  ProductFullDeatilViewModel,
} from "@payping/api";
import {
  createPermalink,
  createProduct,
  getPermalinkDetails,
  getProductDetails,
  updatePermalink,
  updateProduct,
} from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import {
  Affix,
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Form,
  Grid,
  Row,
  Space,
  Tag,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAsync } from "react-use";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch } from "@/utils/redux";
import { CardItemProps } from "@payping/web-components/components/List/types";
import type { RcFile } from "@payping/web-components/components/Upload/types";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import styles from "@/assets/scss/Shop/Product/form.module.scss";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import DrawerCloseButton from "@/components/Buttons/DrawerCloseButton";
import { FormWrapperProps } from "../types";
import LinkForm from "./LinkForm";
import ProductForm from "./ProductForm";
import { LinkFormType, ProductFormTypes } from "./types";

const FormWrapper: React.FC<FormWrapperProps> = (props: FormWrapperProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { code } = useParams<{ code: string }>();

  const { title: pageTitle, locationSearch } = props;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [linkFormRef] = Form.useForm<LinkFormType>();
  const [productFormRef] = Form.useForm<ProductFormTypes>();

  const [kavehNegarSmsText, setKavehNegarSmsText] = useState<string | undefined>();
  const [mailerLiteListId, setMailerLiteListId] = useState<string | undefined>();
  const [productTitle, setProductTitle] = useState<string | undefined>("");
  const [productImageLink, setProductImageLink] = useState<RcFile>();
  const [productImageUrl, setProductImageUrl] = useState<string>();
  const [productAmount, setProductAmount] = useState<number | undefined>();
  const [productQuantity, setProductQuantity] = useState<number | undefined>();
  const [productCategroryCode, setProductCategoryCode] = useState<string | undefined>("");
  const [isMultiple, setIsMultiple] = useState<boolean>(false);
  const [isDirectPaymentActive, setIsDirectPaymentActive] = useState<boolean>(false);

  const { lg } = Grid.useBreakpoint();

  const productItem: CardItemProps["data"] = {
    title: <Typography.Text>{productTitle || t("product.name")}</Typography.Text>,
    content: [
      <Flex key={0} vertical gap={"middle"}>
        <div>
          <Tag>{productCategroryCode || t("product.tag")}</Tag>
        </div>
        <Space className={"tw-flex tw-justify-between"}>
          <Typography.Text>
            {productQuantity} &nbsp;&nbsp;&nbsp; {t("quantity.number")}
          </Typography.Text>
          <Flex align="center">
            <Typography.Text>
              {formatLocalizedNumber({ value: productAmount ?? 0 }) || `( ${t("product.price")} ) `}
              &nbsp;&nbsp;&nbsp;
            </Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        </Space>
      </Flex>,
    ],
    image: { type: "image", src: productImageUrl, size: "large" },
  };

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result;
      if (base64String && typeof base64String === "string") {
        setProductImageUrl(base64String);
      }
    };
    if (productImageLink) reader.readAsDataURL(productImageLink);
  }, [productImageLink]);

  async function validate(): Promise<[boolean, boolean]> {
    try {
      await productFormRef.validateFields();
      await linkFormRef.validateFields();
    } catch (e) {
      const productValidationError = productFormRef
        .getFieldsError()
        .reduce((prev, curr) => prev || Boolean(curr.errors.length), false);
      const linkValidationError = linkFormRef
        .getFieldsError()
        .reduce((prev, curr) => prev || Boolean(curr.errors.length), false);
      return [productValidationError, linkValidationError];
    }
    return [false, false];
  }

  // ======================== PRODUCTS ========================
  // Get Product Details
  const { loading: loadingGetProduct, value: productDetails } = useAsync(async (): Promise<
    ProductFullDeatilViewModel | undefined
  > => {
    if (code) {
      try {
        const pproductDetails = await dispatch(getProductDetails(code));

        setProductTitle(pproductDetails?.title);
        setProductAmount(pproductDetails?.amount);
        setProductQuantity(pproductDetails?.quantity);
        setProductCategoryCode(pproductDetails?.category?.code);

        return pproductDetails;
      } catch {
        /* empty */
      }
    }
    return undefined;
  }, [code]);

  // Create Product
  const [{ loading: loadingCreateProduct }, handleCreateProduct] = useMessagedAsyncFn(
    async (productData: ProductCreateViewModel) => dispatch(createProduct(productData)),
    [],
    undefined,
    { success: false, useProgress: true },
  );
  // Edit Product
  const [{ loading: loadingEditProduct }, handleEditProduct] = useMessagedAsyncFn(
    async (productData: ProductEditViewModel) => {
      await dispatch(updateProduct(productData.code, productData));
    },
    [],
    undefined,
    { success: false, useProgress: true },
  );

  // ======================== PERMA LINK's ========================

  // Get PermaLink Details
  const { loading: loadingGetLink, value: linkDetails } = useAsync(async (): Promise<
    PermanentDetailViewModel | undefined
  > => {
    if (code) {
      try {
        const llinkDetails = await dispatch(getPermalinkDetails(code, { isSilent: true }));
        // reason is IsMultiple is field for link but define in product form and need to manual set
        // isMultiple should only be true when isActive is true
        const isActive = llinkDetails?.isActive ?? false;
        const shouldSetMultiple = isActive && (llinkDetails?.isMultiple || false);
        productFormRef.setFieldValue("isMultiple", shouldSetMultiple);
        setIsMultiple(shouldSetMultiple);
        setIsDirectPaymentActive(isActive);
        setKavehNegarSmsText(llinkDetails.smsText);
        setMailerLiteListId(llinkDetails.mailerLiteListId);
        return llinkDetails;
      } catch {
        /* empty */
      }
    }
    return undefined;
  }, [code]);

  // Create PermaLink
  const [{ loading: loadingCreateLink }, handleCreateLink] = useMessagedAsyncFn(
    async (permaLinkData: PermanentCreateViewModel & { isActive?: Boolean }) => {
      await dispatch(createPermalink(permaLinkData));
    },
    [],
    undefined,
    { success: false, useProgress: true },
  );

  // Edit PermaLink
  const [{ loading: loadingEditLink }, handleEditLink] = useMessagedAsyncFn(
    async (permaLinkData: PermanentEditViewModel & { isActive?: Boolean }) => {
      await dispatch(updatePermalink(permaLinkData.code, permaLinkData));
    },
    [],
    undefined,
    { success: false, useProgress: true },
  );

  //
  const handlePermaLink = async ({
    customDesOption,
    customDesOptionMandatory,
    emailOption,
    nameOption,
    phoneOption,
    address,
    isActive,
    ...values
  }: LinkFormType): Promise<void> => {
    const formValues = {
      ...values,
      customDesOption:
        (customDesOption && customDesOptionMandatory) || PermanentBaseViewModel.CustomDesOptionEnum.DoNotShow,
      emailOption: emailOption
        ? PermanentBaseViewModel.EmailOptionEnum.Required
        : PermanentBaseViewModel.EmailOptionEnum.Optional,
      nameOption: nameOption
        ? PermanentBaseViewModel.NameOptionEnum.Required
        : PermanentBaseViewModel.NameOptionEnum.Optional,
      phoneOption: phoneOption
        ? PermanentBaseViewModel.PhoneOptionEnum.Required
        : PermanentBaseViewModel.PhoneOptionEnum.Optional,
      getAddress: address,
      smsText: kavehNegarSmsText,
      mailerLiteListId,
    };
    // isMultiple should only be true when isActive is true
    const finalIsMultiple = isActive ? isMultiple : false;
    if (!linkDetails?.code) {
      await handleCreateLink({
        ...formValues,
        code: values.productCode,
        isMultiple: finalIsMultiple,
        isActive: isActive,
      });
    } else {
      await handleEditLink({
        ...formValues,
        code: values.productCode,
        isMultiple: finalIsMultiple,
        isActive: isActive,
      });
    }
  };
  // ======================== FINAL ON FINISH ========================
  const onFinishForm = async (values: Record<string, any>): Promise<void> => {
    const { amount, imageLink, quantity, title, unlimited, ...data } = values;
    // isMultiple is link value should not send to product api
    delete data.isMultiple;
    const formValues = {
      ...data,
      amount: parseInt(amount || 0, 10),
      imageLink:
        typeof imageLink === "string"
          ? imageLink.split("/")[imageLink.split("/").length - 1]
          : imageLink?.fileList[0]?.response,
      quantity: unlimited ? 0 : parseInt(quantity || 0, 10),
      unlimited,
      title,
    };
    if (code) {
      await handleEditProduct({ code, ...formValues });
      await handlePermaLink({ ...linkFormRef.getFieldsValue(), productCode: code });
    } else {
      const newProductCode = await handleCreateProduct(formValues);
      await handlePermaLink({ ...linkFormRef.getFieldsValue(), productCode: newProductCode });
    }
    // Go Back To /myShop/product/list
    navigate({
      pathname: DashboardRoutes.myShop.product.list.url,
      search: locationSearch,
    });
  };

  async function onFormSubmitClick() {
    const [productValidationError, linkValidationError] = await validate();

    if (!productValidationError && (!linkFormRef.getFieldValue("isActive") || !linkValidationError)) {
      productFormRef.submit();
    }
  }

  const formLoading = loadingCreateProduct || loadingEditProduct || loadingCreateLink || loadingEditLink;
  const getLoading = loadingGetLink || loadingGetProduct;

  const isDirectPaymentActiveHandler = () => {
    setIsDirectPaymentActive(!isDirectPaymentActive);
  };

  return (
    <Drawer
      open={isDrawerOpen}
      title={pageTitle}
      height={"100%"}
      closeIcon={false}
      placement={"bottom"}
      extra={<DrawerCloseButton setIsDrawerOpen={setIsDrawerOpen} />}
    >
      <HandlingCrispInMobile>
        <Row gutter={[16, 16]} justify={"center"} className={"tw-w-full"}>
          <Col xs={24} lg={12}>
            {/* < فرم ایجاد محصول > */}
            <Form<ProductFormTypes>
              onValuesChange={(changedValues, values) => {
                if ("isMultiple" in changedValues) setIsMultiple(values.isMultiple);
                if ("title" in changedValues) setProductTitle(values.title);
                if ("amount" in changedValues) setProductAmount(values.amount);
                if ("quantity" in changedValues) setProductQuantity(values.quantity);
                if ("categoryCode" in changedValues) setProductCategoryCode(values.categoryCode);
                if ("defineAmountByUser" in changedValues) {
                  setProductAmount(undefined);
                  productFormRef.resetFields(["amount"]);
                }
              }}
              canDeactivate={t("message.beforeSaveCaption", { formName: t("product.title") })}
              className={classNames(styles.form)}
              initialValues={{ defineAmountByUser: false, unlimited: false }}
              onFinish={onFinishForm}
              form={productFormRef}
              name={"product"}
              layout={"vertical"}
            >
              <ProductForm
                setProductImageUrl={setProductImageUrl}
                setProductImageLink={setProductImageLink}
                formRef={productFormRef}
                loading={formLoading || loadingGetProduct}
                details={productDetails}
                isDirectPaymentActive={isDirectPaymentActive}
              />
            </Form>
            {/* < فرم ایجاد محصول /> */}

            {/* < فرم فعالسازی لینک مستقیم پرداخت > */}
            <Form<LinkFormType>
              onValuesChange={(changedValues) => {
                // When isActive changes to false, reset isMultiple to false
                if ("isActive" in changedValues && !changedValues.isActive) {
                  productFormRef.setFieldValue("isMultiple", false);
                  setIsMultiple(false);
                }
              }}
              className={classNames(styles.form)}
              form={linkFormRef}
              name={"productLink"}
              layout={"vertical"}
            >
              <LinkForm
                kavehNegarSmsText={kavehNegarSmsText}
                setKavehNegarSmsText={setKavehNegarSmsText}
                mailerLiteListId={mailerLiteListId}
                setMailerLiteListId={setMailerLiteListId}
                formRef={linkFormRef}
                loading={formLoading || loadingGetLink}
                details={linkDetails}
                onDirectPaymentActiveChange={isDirectPaymentActiveHandler}
              />
            </Form>
            {/* < فرم فعالسازی لینک مستقیم پرداخت /> */}

            {/* < اکشن باتن ها > */}
            <Space>
              <Button type={"primary"} onClick={() => onFormSubmitClick()} loading={formLoading || getLoading}>
                {t("save")}
              </Button>
              <Link to={{ pathname: DashboardRoutes.myShop.product.list.url, search: locationSearch }}>
                <Button type={"default"}>{t("return")}</Button>
              </Link>
            </Space>
            {/* < اکشن باتن ها /> */}
          </Col>

          {/* < نمایش دمو کارت محصول جدید > */}
          {lg ? (
            <Col span={10} offset={2}>
              <div className={"tw-h-full tw-w-full"}>
                <Affix offsetTop={99}>
                  <Card className={"tw-w-full"} data={productItem} />
                </Affix>
              </div>
            </Col>
          ) : null}
          {/* < نمایش دمو کارت محصول جدید /> */}
        </Row>
      </HandlingCrispInMobile>
    </Drawer>
  );
};

export default FormWrapper;
